import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Paper, Button, Tabs, Tab } from '@mui/material';
import allTrendsData from '../../mocks/tendencias.json';
import FiltersBar from '../../components/TrendsDashboard/FiltersBar';
import GaussianBellCurve from '../../components/TrendsDashboard/GaussianBellCurve';
import ComparisonByPeriod from '../../components/TrendsDashboard/ComparisonByPeriod';
import PrePostCalibrationComparison from '../../components/TrendsDashboard/PrePostCalibrationComparison';
import PerformanceByLeader from '../../components/TrendsDashboard/PerformanceByLeader';
import LeaderPerformanceCard from '../../components/TrendsDashboard/LeaderPerformanceCard';
import PerformanceBySeniority from '../../components/TrendsDashboard/PerformanceBySeniority';
import PerformanceVsCulturalFit from '../../components/TrendsDashboard/PerformanceVsCulturalFit';
import ProcessIndicators from '../../components/TrendsDashboard/ProcessIndicators';
import BenchmarkByArea from '../../components/TrendsDashboard/BenchmarkByArea';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LeaderDetailModal from '../../components/TrendsDashboard/LeaderDetailModal';
import PlayerMatrix from '../../components/TrendsDashboard/PlayerMatrix';
import PlayerDetailModal from '../../components/TrendsDashboard/PlayerDetailModal';
import EvaluationDetailModal from '../TeamEvaluations/EvaluationDetailModal';
import allEvaluationsData from '../../mocks/todas_evaluaciones.json'; // Import all evaluations data

export default function TrendsDashboard() {
  const [filters, setFilters] = useState({
    period: '',
    area: [], // Initialize as array for multi-select
    lider: [], // Initialize as array for multi-select
  });

  const [isLeaderModalOpen, setIsLeaderModalOpen] = useState(false);
  const [selectedLeaderData, setSelectedLeaderData] = useState(null);
  const [isPlayerDetailModalOpen, setIsPlayerDetailModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [selectedEvaluationDetail, setSelectedEvaluationDetail] = useState(null);
  const [selectedLeaderForGaussianBellCurve, setSelectedLeaderForGaussianBellCurve] = useState(''); // New state for selected leader in Gaussian Bell Curve
  const [selectedTab, setSelectedTab] = useState(0); // State for managing tabs

  const filteredTrendsData = useMemo(() => {
    const selectedPeriodData = allTrendsData.tendencias_data.find(d => d.periodo === filters.period);
    
    if (!selectedPeriodData) return null;

    let filteredMetrics = {
      ...selectedPeriodData,
      metricas_por_area: filters.area.length > 0
        ? selectedPeriodData.metricas_por_area.filter(m => filters.area.includes(m.area))
        : selectedPeriodData.metricas_por_area,
      metricas_por_lider: filters.lider.length > 0
        ? selectedPeriodData.metricas_por_lider.filter(m => filters.lider.includes(m.lider))
        : selectedPeriodData.metricas_por_lider,
      colaboradores: selectedPeriodData.colaboradores.filter(c => {
        const matchesArea = filters.area.length === 0 || filters.area.includes(c.area);
        const matchesLider = filters.lider.length === 0 || filters.lider.includes(c.lider);
        return matchesArea && matchesLider;
      }),
    };

    return filteredMetrics;
  }, [filters.period, filters.area, filters.lider, allTrendsData.tendencias_data]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const periodOptions = useMemo(() => {
    return Array.from(new Set(allTrendsData.tendencias_data.map(d => d.periodo))).sort();
  }, []);

  const liderOptions = useMemo(() => {
    if (!allTrendsData || !allTrendsData.tendencias_data) return [];
    const lidersFromMetrics = allTrendsData.tendencias_data.flatMap(d => d.metricas_por_lider.map(m => m.lider));
    const lidersFromCollaborators = allTrendsData.tendencias_data.flatMap(d => d.colaboradores.map(c => c.lider));
    return Array.from(new Set([...lidersFromMetrics, ...lidersFromCollaborators])).filter(Boolean).sort();
  }, [allTrendsData.tendencias_data]);

  useEffect(() => {
    if (periodOptions.length > 0 && !filters.period) {
      setFilters(f => ({ ...f, period: periodOptions[periodOptions.length - 1] }));
    }
  }, [periodOptions, filters.period]);

  useEffect(() => {
    if (liderOptions.length > 0 && !selectedLeaderForGaussianBellCurve) {
      setSelectedLeaderForGaussianBellCurve(liderOptions[0]);
    }
  }, [liderOptions, selectedLeaderForGaussianBellCurve]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleLeaderClick = (leaderName) => {
    const leader = filteredTrendsData?.metricas_por_lider.find(l => l.lider === leaderName);
    if (leader) {
      setSelectedLeaderData({ leaderName: leader.lider, distribution: leader.distribucion_puntaje });
      setIsLeaderModalOpen(true);
    }
  };

  const handleCloseLeaderModal = () => {
    setIsLeaderModalOpen(false);
    setSelectedLeaderData(null);
  };

  const handlePlayerClick = (playerData) => {
    setSelectedPlayerData(playerData);
    setIsPlayerDetailModalOpen(true);
  };

  const handleClosePlayerDetailModal = () => {
    setIsPlayerDetailModalOpen(false);
    setSelectedPlayerData(null);
  };

  const handleViewDetails = (evaluationData) => {
    setSelectedEvaluationDetail(evaluationData);
  };

  // Function to classify player type based on scores (moved from PerformanceByLeader)
  const classifyPlayer = (performance, culturalFit) => {
    if (performance >= 1 && performance <= 3 && culturalFit >= 1 && culturalFit <= 2) {
      return 'Jugador A'; // Altamente alineados
    } else if (performance >= 1 && performance <= 3 && culturalFit >= 3 && culturalFit <= 4) {
      return 'Jugador B'; // Buen rendimiento, bajo fit
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 1 && culturalFit <= 2) {
      return 'Jugador C'; // Buen fit, bajo rendimiento
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 3 && culturalFit <= 4) {
      return 'Jugador D'; // Doble riesgo
    }
    return 'Desconocido';
  };

  const leaderAnalysis = useMemo(() => {
    if (!filteredTrendsData?.colaboradores) return {};

    const analysis = {};

    filteredTrendsData.colaboradores.forEach(collab => {
      if (!analysis[collab.lider]) {
        analysis[collab.lider] = {
          jugadorA: 0,
          jugadorC: 0,
          performanceScores: [],
          culturalFitScores: [],
        };
      }

      const playerType = classifyPlayer(collab.puntaje_desempeno, collab.puntaje_fit_cultural);
      if (playerType === 'Jugador A') {
        analysis[collab.lider].jugadorA++;
      } else if (playerType === 'Jugador C') {
        analysis[collab.lider].jugadorC++;
      }

      analysis[collab.lider].performanceScores.push(collab.puntaje_desempeno);
      analysis[collab.lider].culturalFitScores.push(collab.puntaje_fit_cultural);
    });

    // Calculate discrepancy
    Object.keys(analysis).forEach(lider => {
      const perfScores = analysis[lider].performanceScores;
      const fitScores = analysis[lider].culturalFitScores;

      const perfRange = perfScores.length > 1 ? Math.max(...perfScores) - Math.min(...perfScores) : 0;
      const fitRange = fitScores.length > 1 ? Math.max(...fitScores) - Math.min(...fitScores) : 0;

      // Simple sum of ranges for a combined discrepancy score
      analysis[lider].discrepancy = perfRange + fitRange;
    });

    return analysis;
  }, [filteredTrendsData?.colaboradores]);

  const topJugadorALeader = useMemo(() => {
    if (!leaderAnalysis || Object.keys(leaderAnalysis).length === 0) return null;
    return Object.entries(leaderAnalysis).sort(([, a], [, b]) => b.jugadorA - a.jugadorA)[0];
  }, [leaderAnalysis]);

  const topJugadorCLeader = useMemo(() => {
    if (!leaderAnalysis || Object.keys(leaderAnalysis).length === 0) return null;
    return Object.entries(leaderAnalysis).sort(([, a], [, b]) => b.jugadorC - a.jugadorC)[0];
  }, [leaderAnalysis]);

  const highestDiscrepancyLeader = useMemo(() => {
    if (!leaderAnalysis || Object.keys(leaderAnalysis).length === 0) return null;
    return Object.entries(leaderAnalysis).sort(([, a], [, b]) => b.discrepancy - a.discrepancy)[0];
  }, [leaderAnalysis]);

  const lowestDiscrepancyLeader = useMemo(() => {
    if (!leaderAnalysis || Object.keys(leaderAnalysis).length === 0) return null;
    const nonZeroDiscrepancyLeaders = Object.entries(leaderAnalysis).filter(([, a]) => a.discrepancy > 0);
    if (nonZeroDiscrepancyLeaders.length > 0) {
      return nonZeroDiscrepancyLeaders.sort(([, a], [, b]) => a.discrepancy - b.discrepancy)[0];
    }
    return Object.entries(leaderAnalysis).sort(([, a], [, b]) => a.discrepancy - b.discrepancy)[0];
  }, [leaderAnalysis]);

  const areaOptions = useMemo(() => {
    if (!allTrendsData) return [];
    const areasFromMetrics = allTrendsData.tendencias_data.flatMap(d => d.metricas_por_area.map(m => m.area));
    const areasFromCollaborators = allTrendsData.tendencias_data.flatMap(d => d.colaboradores.map(c => c.area));
    return Array.from(new Set([...areasFromMetrics, ...areasFromCollaborators])).filter(Boolean).sort();
  }, [allTrendsData.tendencias_data]);

  const currentPeriodAverageScore = filteredTrendsData?.metricas_generales?.puntaje_promedio_calibrado || 0;

  const previousPeriodAverageScore = useMemo(() => {
    if (!filters.period || periodOptions.length === 0) return 0;
    const currentIndex = periodOptions.indexOf(filters.period);
    if (currentIndex > 0) {
      const previousPeriod = periodOptions[currentIndex - 1];
      const previousPeriodData = allTrendsData.tendencias_data.find(d => d.periodo === previousPeriod);
      return previousPeriodData?.metricas_generales?.puntaje_promedio_calibrado || 0;
    }
    return 0;
  }, [filters.period, periodOptions, allTrendsData.tendencias_data]);

  const scoreDifference = currentPeriodAverageScore - previousPeriodAverageScore;
  const scoreChangeText = previousPeriodAverageScore > 0 
    ? `${scoreDifference >= 0 ? '+' : ''}${scoreDifference.toFixed(2)}` 
    : `${scoreDifference.toFixed(2)}`;

  const handleExportReport = () => {
    alert('Funcionalidad de exportación de reporte en desarrollo.');
  };

  const warningCases = useMemo(() => {
    if (!filteredTrendsData?.colaboradores) return [];
    return filteredTrendsData.colaboradores.filter(c => 
      c.ultima_evaluacion.includes('Doble Riesgo') || 
      c.ultima_evaluacion.includes('Desempeño Débil') ||
      c.ultima_evaluacion.includes('Reevaluación urgente') ||
      c.ultima_evaluacion.includes('Desempeño Crítico') ||
      c.ultima_evaluacion.includes('Reubicación o desvinculación') ||
      c.ultima_evaluacion.includes('Fit Cultural Bajo')
    ).map(c => ({
      name: c.nombre,
      initials: c.iniciales,
      evaluation: c.ultima_evaluacion,
      feedback: c.feedback_textual,
    }));
  }, [filteredTrendsData]);

  return (
    <Box p={4} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>📈 Análisis de Desempeño Calibrado (Prueba de Cambio) – {filters.period || 'Seleccione Período'}</Typography>
        <Button variant="contained" startIcon={<ArrowDownwardIcon />} onClick={handleExportReport}>
          Exportar Reporte
        </Button>
      </Box>

      <FiltersBar
        filters={filters}
        periodOptions={periodOptions}
        areaOptions={areaOptions}
        liderOptions={liderOptions}
        onFilterChange={handleFilterChange}
      />

      {!filters.period ? (
        <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
          <Typography variant="h6">Seleccione un período en los filtros para ver las tendencias de desempeño.</Typography>
        </Paper>
      ) : (
        <>
          {warningCases.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', borderColor: 'error.main', borderStyle: 'solid', borderWidth: '2px' }}>
                <Typography variant="h5" mb={2} sx={{ fontWeight: 'bold', color: 'error.main' }}>🚨 Casos que Necesitan Atención</Typography>
                <Grid container spacing={1}>
                  {warningCases.map((wc, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper variant="outlined" sx={{ p: 1, borderColor: 'error.light' }}>
                        <Typography variant="subtitle2" fontWeight="bold">{wc.name} ({wc.initials})</Typography>
                        <Typography variant="body2" color="text.secondary">{wc.evaluation}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" mt={4} mb={2}>Campana de Gauss de Puntaje Calibrado</Typography>
              <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', minHeight: '900px', width: '100%' }}>
                <GaussianBellCurve 
                  distribution={filteredTrendsData?.metricas_generales?.distribucion_puntaje}
                  title="Distribución General"
                />
              </Paper>
            </Grid>
          </Grid>

          <Tabs value={selectedTab} onChange={handleTabChange} aria-label="dashboard tabs"
            sx={{ 
              mt: 4, 
              mb: 2,
              '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 'bold' }
            }}>
            <Tab label="Comparativa por Período" />
            <Tab label="Matriz de Jugadores" />
            <Tab label="Comparativa Pre vs Post Calibración" />
            <Tab label="Indicadores de Proceso" />
            <Tab label="Desempeño por Líder" />
            <Tab label="Desempeño por Seniority" />
            <Tab label="Matriz Desempeno vs Fit Cultural" />
            <Tab label="Benchmark por Área" />
          </Tabs>

          {selectedTab === 0 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>🔄 Comparativa por Período</Typography>
              <ComparisonByPeriod allTrendsData={allTrendsData.tendencias_data} periodOptions={periodOptions} />
            </Box>
          )}
          {selectedTab === 1 && (
            <Box>
              <PlayerMatrix 
                collaborators={filteredTrendsData?.colaboradores}
                onPlayerClick={handlePlayerClick}
                areaFilter={filters.area}
                liderFilter={filters.lider}
              />
            </Box>
          )}
          {selectedTab === 2 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>⚖ Comparativa Pre vs Post Calibración</Typography>
              <PrePostCalibrationComparison 
                originalDistribution={filteredTrendsData?.metricas_generales?.original_distribucion_puntaje}
                calibratedDistribution={filteredTrendsData?.metricas_generales?.distribucion_puntaje}
              />
            </Box>
          )}
          {selectedTab === 3 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>8. Indicadores de Proceso</Typography>
              <ProcessIndicators data={filteredTrendsData?.metricas_generales?.process_indicators} />
            </Box>
          )}
          {selectedTab === 4 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>Resumen de Líderes</Typography>
              <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', mb: 4 }}>
                {topJugadorALeader && (
                  <Typography variant="body1">Líder con más Jugadores A: **{topJugadorALeader[0]}** ({topJugadorALeader[1].jugadorA} jugadores)</Typography>
                )}
                {topJugadorCLeader && (
                  <Typography variant="body1">Líder con más Jugadores C: **{topJugadorCLeader[0]}** ({topJugadorCLeader[1].jugadorC} jugadores)</Typography>
                )}
                {highestDiscrepancyLeader && (
                  <Typography variant="body1">Líder con mayor discrepancia interna: **{highestDiscrepancyLeader[0]}** (Discrepancia: {highestDiscrepancyLeader[1].discrepancy.toFixed(2)})</Typography>
                )}
                {lowestDiscrepancyLeader && (
                  <Typography variant="body1">Líder con menor discrepancia interna: **{lowestDiscrepancyLeader[0]}** (Discrepancia: {lowestDiscrepancyLeader[1].discrepancy.toFixed(2)})</Typography>
                )}
                {(!topJugadorALeader && !topJugadorCLeader && !highestDiscrepancyLeader && !lowestDiscrepancyLeader) && (
                  <Typography variant="body2" color="text.secondary">No hay datos suficientes para generar el resumen de líderes.</Typography>
                )}
              </Paper>

              <Typography variant="h5" mb={2}>📈 Desempeño por Líder</Typography>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="leader-select-label">Seleccionar Líder</InputLabel>
                <Select
                  labelId="leader-select-label"
                  value={selectedLeaderForGaussianBellCurve}
                  label="Seleccionar Líder"
                  onChange={(e) => setSelectedLeaderForGaussianBellCurve(e.target.value)}
                >
                  {liderOptions.map((lider) => (
                    <MenuItem key={lider} value={lider}>
                      {lider}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <PerformanceByLeader 
                leaderName={selectedLeaderForGaussianBellCurve}
                allEvaluations={allEvaluationsData?.filter(e => e.detalles?.semestre === filters.period) || []} // Pass all evaluations for the current period
              />
            </Box>
          )}
          {selectedTab === 5 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>🧓 Desempeño por Seniority</Typography>
              <PerformanceBySeniority data={filteredTrendsData?.metricas_por_seniority} />
            </Box>
          )}
          {selectedTab === 6 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>🧩 Matriz Desempeno vs Fit Cultural</Typography>
              <PerformanceVsCulturalFit data={filteredTrendsData?.metricas_desempeno_vs_fit_cultural} />
            </Box>
          )}
          {selectedTab === 7 && (
            <Box>
              <Typography variant="h5" mt={4} mb={2}>9. Benchmark por Área</Typography>
              <BenchmarkByArea data={filteredTrendsData?.metricas_por_area} />
            </Box>
          )}
        </>
      )}
      {selectedLeaderData && (
        <LeaderDetailModal
          open={isLeaderModalOpen}
          handleClose={handleCloseLeaderModal}
          leaderName={selectedLeaderData.leaderName}
          distribution={selectedLeaderData.distribution}
        />
      )}
      {selectedPlayerData && (
        <PlayerDetailModal
          open={isPlayerDetailModalOpen}
          handleClose={handleClosePlayerDetailModal}
          player={selectedPlayerData}
          onViewDetails={handleViewDetails}
        />
      )}
      {selectedEvaluationDetail && (
        <EvaluationDetailModal
          open={!!selectedEvaluationDetail}
          onClose={() => setSelectedEvaluationDetail(null)}
          evaluation={selectedEvaluationDetail}
        />
      )}
    </Box>
  );
} 